import React from 'react';
import { Card, Tag, Typography } from 'antd';
import type { CaseStudyTldr, ModelImpactAnalysis } from '../types/analyticsModel';
import './CaseStudyTldr.css';

const { Title, Paragraph, Text } = Typography;

const signed = (value: number, suffix = '') =>
  `${value > 0 ? '+' : ''}${value.toFixed(1)}${suffix}`;

const CaseStudyTldrView: React.FC<{
  tldr: CaseStudyTldr;
  model: ModelImpactAnalysis;
}> = ({ tldr, model }) => {
  const maximumImportance = Math.max(
    1,
    ...model.featureImportance.map(feature => feature.playoffImportance));

  return (
    <section className="case-tldr" aria-label="TLDR evidence and ranked assessment">

      <div className="case-tldr-metrics">
        {tldr.keyNumbers.map(metric => (
          <Card bordered={false} className={`case-tldr-metric case-tldr-metric--${metric.tone}`} key={metric.label}>
            <Text>{metric.label}</Text>
            <strong>{metric.value}</strong>
            <small>{metric.detail}</small>
          </Card>
        ))}
      </div>

      <div className="case-tldr-grid">
        <Card bordered={false} className="case-tldr-drivers">
          <Title level={3}>Ranked assessment</Title>
          <Paragraph>The strongest period-level impacts, ordered by comparable model and scenario magnitude.</Paragraph>
          <ol>
            {tldr.rankedDrivers.slice(0, 7).map(driver => (
              <li key={driver.key}>
                <span className={`case-tldr-rank case-tldr-rank--${driver.direction}`}>{driver.rank}</span>
                <div>
                  <div className="case-tldr-driver-title">
                    <strong>{driver.label}</strong>
                    <Tag>{driver.assessment}</Tag>
                  </div>
                  <div className="case-tldr-impact-values">
                    {driver.impactWins !== 0 && <span>{signed(driver.impactWins, ' wins')}</span>}
                    {driver.impactPointsPerGame !== 0 && <span>{signed(driver.impactPointsPerGame, ' PPG')}</span>}
                    {driver.impactPlayoffPercentagePoints !== 0
                      && <span>{signed(driver.impactPlayoffPercentagePoints, ' playoff pp')}</span>}
                  </div>
                  <small>{driver.summary} {driver.source}.</small>
                </div>
              </li>
            ))}
          </ol>
        </Card>

        <Card bordered={false} className="case-tldr-importance">
          <Title level={3}>What matters league-wide</Title>
          <Paragraph>Mean absolute playoff SHAP magnitude across all team-seasons.</Paragraph>
          <div className="case-tldr-bars">
            {model.featureImportance.slice(0, 8).map(feature => (
              <div className="case-tldr-bar" key={feature.key}>
                <div>
                  <span>{feature.label}</span>
                  <strong>{feature.playoffImportance.toFixed(1)} pp</strong>
                </div>
                <i>
                  <b style={{ width: `${(feature.playoffImportance / maximumImportance) * 100}%` }} />
                </i>
              </div>
            ))}
          </div>
          <Text className="case-tldr-model-note">
            {model.modelType} � {model.trainingStartYear}-{model.trainingEndYear} � {model.observationCount} observations
          </Text>
        </Card>
      </div>
      <Text className="case-tldr-caveat">{tldr.modelCaveat}</Text>
    </section>
  );
};

export default CaseStudyTldrView;
